import "./AddProducts.scss";
import { useState, useEffect } from "react";
import {
  InputAdornment,
  TextField,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AddProductsColumns } from "../../SourceData";
import SearchIcon from "@mui/icons-material/Search";
import CancelIcon from "@mui/icons-material/ClearOutlined";
import AcceptIcon from "@mui/icons-material/CheckOutlined";
import MessageModals from "../../components/MessageModals/MessageModals";
import ShowUser from "./../../assets/show-user.svg";
import UpdateDetailsModal from "./UpdateDetailsModal";
import api from "../../utils/api";
import { toast } from "react-toastify";
import usePermissions from "../../hooks/usePermissions";

const AddProducts = () => {
  const isMobile = useMediaQuery("(max-width:767px)");
  const isTablet = useMediaQuery("(min-width: 768px) and (max-width: 1024px)");
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [openMessage, setOpenMessage] = useState(false);
  const [currentView, setCurrentView] = useState("");
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalRows, setTotalRows] = useState(0);
  const [selectedProductId, setSelectedProductId] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [updateModalOpen, setUpdateModalOpen] = useState(false);
  const [selectedChangeRequest, setSelectedChangeRequest] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    getData();
  }, [query, page, pageSize]);
  useEffect(() => {
    setPage(0);
  }, [filterType]);

  const getData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page + 1, limit: pageSize });
      if (query) params.append("search", query);
      const res = await api.get(`/products-requests?${params.toString()}`);
      setProducts(res.data.data);
      setTotalRows(res.data.total);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter((p) => {
    if (filterType === "new") return p.latest_model_change_request === null;
    if (filterType === "update") return p.latest_model_change_request !== null;
    return true;
  });

  const handleMessage = (view, productId) => {
    setSelectedProductId(productId);
    setCurrentView(view);
    setOpenMessage(true);
  };

  const handleShowUpdateDetails = (product) => {
    setSelectedChangeRequest(product.latest_model_change_request);
    setSelectedProduct(product);
    setUpdateModalOpen(true);
  };

  const actionColumn = [
    hasPermission(201) && {
      field: "action",
      headerName: "",
      width: 220,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const hasChangeRequest =
          params.row.latest_model_change_request !== null;
        return (
          <div className="cell-action">
            {hasChangeRequest && (
              <div
                className="icon-container"
                onClick={() => handleShowUpdateDetails(params.row)}
                title="عرض تفاصيل التعديل"
              >
                <img src={ShowUser} alt="show" className="icon" />
              </div>
            )}
            <div
              className="icon-container"
              style={{ backgroundColor: "#E63050" }}
              onClick={() => handleMessage("reject", params.row.id)}
            >
              <CancelIcon className="icon" style={{ color: "white" }} />
            </div>
            <div
              className="icon-container"
              style={{ backgroundColor: "#28B446" }}
              onClick={() => handleMessage("accept", params.row.id)}
            >
              <AcceptIcon className="icon" style={{ color: "white" }} />
            </div>
          </div>
        );
      },
    },
  ].filter(Boolean);

  return (
    <div className="users">
      <div className="special-header">
        <p className="title">طلبات اضافة و تعديل المنتجات</p>
        <div
          className="actions-container"
          style={{
            gap: 10,
            display: "flex",
            justifyContent: isMobile ? "center" : "flex-start",
          }}
        >
          <div className="search">
            <TextField
              id="outlined-search"
              type="search"
              size="small"
              value={query}
              placeholder="البحث باسم المنتج او المتجر/المصنع"
              onChange={(e) => setQuery(e.target.value)}
              sx={{
                width: isMobile ? "100%" : isTablet ? "300px" : "360px",
                borderRadius: "8px",
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>نوع الطلب</InputLabel>
            <Select
              value={filterType}
              label="نوع الطلب"
              onChange={(e) => setFilterType(e.target.value)}
            >
              <MenuItem value="all">الكل</MenuItem>
              <MenuItem value="new">جديد</MenuItem>
              <MenuItem value="update">تعديل</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      <div className="container">
        <DataGrid
          sx={{ backgroundColor: "white", width: "100%" }}
          autoHeight
          columns={AddProductsColumns.concat(actionColumn)}
          rows={filteredProducts.map((row, index) => ({
            ...row,
            index: page * pageSize + index + 1,
          }))}
          disableVirtualization
          disableColumnFilter
          disableColumnMenu
          loading={loading}
          rowHeight={80}
          paginationMode="server"
          pageSizeOptions={[10, 25, 50]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(model) => {
            setPage(model.page);
            setPageSize(model.pageSize);
          }}
          rowCount={totalRows}
          getRowId={(row) => row.id}
        />
      </div>

      <MessageModals
        open={openMessage}
        currentView={currentView}
        onClose={() => {
          setOpenMessage(false);
          setSelectedProductId(null);
        }}
        itemId={selectedProductId}
        onDataChange={getData}
        text="تم تحديث حالة المنتج بنجاح"
        acceptRoute="/action-products-requests"
        rejectRoute="/action-products-requests"
        acceptData={{ active: 1, product_id: selectedProductId }}
        rejectData={{ active: 2, product_id: selectedProductId }}
        withReason={true}
      />

      <UpdateDetailsModal
        open={updateModalOpen}
        onClose={() => {
          setUpdateModalOpen(false);
          setSelectedChangeRequest(null);
          setSelectedProduct(null);
        }}
        changeRequest={selectedChangeRequest}
        currentProduct={selectedProduct}
      />
    </div>
  );
};

export default AddProducts;
